import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQuery } from "@tanstack/react-query";

type UserPreferences = {
  id: string;
  user_id: string;
  default_currency_id?: string;
  exchange_provider?: string;
  importanceColors?: string;
  company_register_provider?: string;
  bolagsverket_api_key?: string;
  companieshouse_api_key?: string;
  openexchangerates_api_key?: string;
  "exchangerate-api_api_key"?: string;
  exchangeratesapi_api_key?: string;
  freecurrencyapi_api_key?: string;
  created_at?: string;
  updated_at?: string;
  setup_completed?: boolean;
  
  // Adding camelCase aliases for convenience
  defaultCurrencyId?: string;
  showGroupedContracts?: boolean;
};

// Helper function to convert snake_case to camelCase for preferences
const formatPreferences = (preferences: UserPreferences | null): UserPreferences | null => {
  if (!preferences) return null;
  
  return {
    ...preferences,
    // Add camelCase aliases
    defaultCurrencyId: preferences.default_currency_id,
    showGroupedContracts: preferences.showGroupedContracts
  };
};

export function useUserPreferences() {
  const { user, isAuthenticated } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  
  useEffect(() => {
    const loadPreferences = async () => {
      if (!isAuthenticated || !user) {
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      
      try {
        const { data, error } = await supabase
          .from("user_preferences")
          .select("*")
          .eq("user_id", user.id)
          .single();
          
        if (error && error.code !== "PGRST116") { // PGRST116 is "no rows returned" error
          console.error("Error loading user preferences:", error);
          return;
        }
        
        if (data) {
          setPreferences(formatPreferences(data));
        } else {
          // Create default preferences if none exist
          const { data: newPrefs, error: insertError } = await supabase
            .from("user_preferences")
            .insert({ user_id: user.id })
            .select("*")
            .single();
            
          if (insertError) {
            console.error("Error creating default preferences:", insertError);
            return;
          }
          
          setPreferences(formatPreferences(newPrefs));
        }
      } catch (error) {
        console.error("Error loading user preferences:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadPreferences();
  }, [isAuthenticated, user]);
  
  const updatePreferences = async (updates: Partial<UserPreferences>) => {
    if (!user || !preferences) {
      throw new Error("User not authenticated or preferences not loaded");
    }
    
    setIsUpdating(true);
    
    try {
      // Convert any camelCase properties back to snake_case for the database
      const dbUpdates: Partial<UserPreferences> = { ...updates };
      if ('defaultCurrencyId' in updates && updates.defaultCurrencyId !== undefined) {
        dbUpdates.default_currency_id = updates.defaultCurrencyId;
        delete dbUpdates.defaultCurrencyId;
      }
      
      const { data, error } = await supabase
        .from("user_preferences")
        .update(dbUpdates)
        .eq("user_id", user.id)
        .select("*")
        .single();
        
      if (error) {
        console.error("Error updating user preferences:", error);
        throw error;
      }
      
      setPreferences(formatPreferences(data));
      return formatPreferences(data);
    } finally {
      setIsUpdating(false);
    }
  };
  
  const fetchUserPreferences = async () => {
    if (!user || !isAuthenticated) {
      return null;
    }
    
    const { data, error } = await supabase
      .from("user_preferences")
      .select("*")
      .eq("user_id", user.id)
      .single();
      
    if (error && error.code !== "PGRST116") {
      throw error;
    }
    
    if (!data) {
      // Create default preferences if none exist
      const { data: newPrefs, error: insertError } = await supabase
        .from("user_preferences")
        .insert({ user_id: user.id })
        .select("*")
        .single();
        
      if (insertError) {
        throw insertError;
      }
      
      return formatPreferences(newPrefs);
    }
    
    return formatPreferences(data);
  };
  
  const useUserPreferencesQuery = () => {
    return useQuery({
      queryKey: ['userPreferences', user?.id],
      queryFn: fetchUserPreferences,
      enabled: !!user && !!isAuthenticated,
    });
  };
  
  const useUpdateImportanceColorsMutation = () => {
    return useMutation({
      mutationFn: async (importanceColors: string) => {
        if (!user) {
          throw new Error("User not authenticated");
        }
        
        const { data, error } = await supabase
          .from("user_preferences")
          .update({ importanceColors })
          .eq("user_id", user.id)
          .select("*")
          .single();
          
        if (error) {
          throw error;
        }
        
        return formatPreferences(data);
      }
    });
  };
  
  return {
    preferences,
    updatePreferences,
    isLoading,
    isUpdating,
    useUserPreferencesQuery,
    useUpdateImportanceColorsMutation
  };
}
